import { LOOK_KEY_PATTERN, LOOK_SLOTS, type LookPiece, type LookSlot } from "../rules/look-records.ts";
import { LOOK_LAYOUT_VERSION, LOOK_RANKS, lookSheetIds, type LookComposeLayer, type LookLayout, type LookLayoutCell, type LookLayoutStrip, type LookRank } from "./look-layout.ts";
import { DIRECTION_NAMES, type DirectionName } from "./sheet.ts";

/**
 * The look sheets' pointer (ADR-094 decision 13): `assets/looks/current.json`, which the asset build writes and a page
 * reads before it composes a look. It holds the block layout every sheet shares and, for every sheet of every piece
 * of the looks table, the content-hashed path of its PNG relative to the asset base, with the catalogue's beside
 * them. The sheets are named by id (`lookSheetIds`): a body layer's piece is one sheet, a hand piece four.
 *
 * A page reads this document from the network, so the validators are strict. A field they do not know is refused, at
 * every level: the build is deterministic and nothing legitimate adds one. Every count and every number is held to a
 * bound (`LOOK_LAYOUT_LIMITS`), so a document cannot make a reader allocate without limit. A strip's name is held to
 * a plain pattern and the strips are returned in a record with no prototype, so no name in the document reaches
 * `Object.prototype`. What passes is rebuilt field by field in one order, so it serialises to the same bytes whatever
 * order its fields arrived in, and a page never draws from a cell outside the block, a frame that names no cell or a
 * path that is not a hashed file under `assets/looks`.
 *
 * Shape is not completeness: `lookManifestProblems` holds a pointer against a looks table, and a publisher refuses
 * one that lacks a piece's sheet or names a sheet of no piece.
 */

/**
 * Where the look sheets and their pointer sit, relative to the asset base and under `dist/`.
 */
export const LOOK_SHEETS_DIR = "assets/looks";

/**
 * The most a layout or a pointer may hold. The layout cut on 2026-10-04 is a block of 512 by 320 with 120 cells, 10
 * strips of at most 12 frames a direction at 1 to 24 frames a second, a gutter of 1, a head of 32, offsets from -44
 * to -7 and 12 layers, under a pointer of 64 sheets; three combat sets roughly triple the cells (decision 15).
 * `block` is a look page's side, which a block must fit, and bounds the head and a cell's offsets too.
 */
export const LOOK_LAYOUT_LIMITS = Object.freeze({ block: 2048, gutter: 16, cells: 1024, strips: 64, frames: 64, fps: 60, compose: 32, sheets: 4096 });

/**
 * A strip's name: 1 to 32 characters of `a-z`, `0-9` and `_`, starting with a letter. `constructor` and `prototype`
 * fit the pattern and are refused by name.
 */
const STRIP_NAME = /^[a-z][a-z0-9_]{0,31}$/;
const REFUSED_STRIP_NAMES: readonly string[] = ["constructor", "prototype"];

/**
 * The pointer: the layout, each sheet's hashed path by sheet id, and the catalogue's hashed path.
 */
export interface LookManifest {
  readonly layout: LookLayout;
  readonly sheets: Readonly<Record<string, string>>;
  readonly catalogue: string;
}

const HASH = "[0-9a-f]{16}";
const CATALOGUE_PATH = new RegExp(`^${LOOK_SHEETS_DIR}/catalogue\\.${HASH}\\.json$`);
const SHEET_HASH = new RegExp(`^${HASH}$`);

/**
 * The path of a sheet's file.
 *
 * @param sheetId The sheet's id (`lookSheetIds`).
 * @param hash The 16 hex digits of its content hash.
 * @returns The path relative to the asset base.
 * @example
 * lookSheetPath("humn-3", "0123456789abcdef");
 * // "assets/looks/humn-3.0123456789abcdef.png"
 */
export function lookSheetPath(sheetId: string, hash: string): string {
  return `${LOOK_SHEETS_DIR}/${sheetId}.${hash}.png`;
}

/**
 * The path of the catalogue's file.
 *
 * @param hash The 16 hex digits of its content hash.
 * @returns The path relative to the asset base.
 */
export function lookCataloguePath(hash: string): string {
  return `${LOOK_SHEETS_DIR}/catalogue.${hash}.json`;
}

/**
 * Narrows to a plain object that holds no field but those named.
 *
 * @param value The value.
 * @param where Its name, for the error.
 * @param known The fields it may hold, or null for a record whose keys the caller checks.
 * @returns The object.
 * @throws Error when it is not an object, or naming the first field it holds that is not known.
 */
function asObject(value: unknown, where: string, known: readonly string[] | null): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${where} must be an object`);
  }
  const unknown = known === null ? undefined : Object.keys(value).find((key) => !known.includes(key));
  if (unknown !== undefined) {
    throw new Error(`${where} has an unknown field ${unknown}`);
  }
  return value as Record<string, unknown>;
}

/**
 * Narrows to an array of a bounded length.
 *
 * @param value The value.
 * @param where Its name, for the error.
 * @param most The most entries it may hold.
 * @returns The array.
 * @throws Error when it is not an array or holds more.
 */
function asArray(value: unknown, where: string, most: number): unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`${where} must be a list`);
  }
  if (value.length > most) {
    throw new Error(`${where} must hold at most ${most}`);
  }
  return value;
}

/**
 * Narrows to a whole number within a range.
 *
 * @param value The value.
 * @param where Its name, for the error.
 * @param least The smallest value allowed.
 * @param most The largest value allowed.
 * @returns The number.
 * @throws Error when it is not a whole number in the range.
 */
function whole(value: unknown, where: string, least: number, most: number): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < least || value > most) {
    throw new Error(`${where} must be a whole number from ${least} to ${most}`);
  }
  return value;
}

/**
 * Reads one cell and holds it inside the block.
 *
 * @param value The parsed cell.
 * @param where Its name, for the error.
 * @param block The block's size.
 * @returns The cell.
 * @throws Error naming the field that is unknown or out of range, or the cell when it lies outside the block.
 */
function cellOf(value: unknown, where: string, block: LookLayout["block"]): LookLayoutCell {
  const fields = asObject(value, where, ["x", "y", "width", "height", "offsetX", "offsetY"]);
  const reach = LOOK_LAYOUT_LIMITS.block;
  const cell: LookLayoutCell = {
    x: whole(fields["x"], `${where}.x`, 0, reach),
    y: whole(fields["y"], `${where}.y`, 0, reach),
    width: whole(fields["width"], `${where}.width`, 1, reach),
    height: whole(fields["height"], `${where}.height`, 1, reach),
    offsetX: whole(fields["offsetX"], `${where}.offsetX`, -reach, reach),
    offsetY: whole(fields["offsetY"], `${where}.offsetY`, -reach, reach),
  };
  if (cell.x + cell.width > block.width || cell.y + cell.height > block.height) {
    throw new Error(`${where} lies outside the ${block.width} by ${block.height} block`);
  }
  return cell;
}

/**
 * Reads one strip: its rate and, for each direction, frames that each name a cell.
 *
 * @param value The parsed strip.
 * @param where Its name, for the error.
 * @param cells How many cells the layout holds.
 * @returns The strip.
 * @throws Error naming the unknown field, the rate, the direction or the frame at fault.
 */
function stripOf(value: unknown, where: string, cells: number): LookLayoutStrip {
  const fields = asObject(value, where, ["fps", "frames"]);
  const fps = fields["fps"];
  if (typeof fps !== "number" || !Number.isFinite(fps) || fps <= 0 || fps > LOOK_LAYOUT_LIMITS.fps) {
    throw new Error(`${where}.fps must be a rate above 0 and at most ${LOOK_LAYOUT_LIMITS.fps}`);
  }
  const byDirection = asObject(fields["frames"], `${where}.frames`, DIRECTION_NAMES);
  const frames = {} as Record<DirectionName, readonly number[]>;
  for (const direction of DIRECTION_NAMES) {
    const listed = asArray(byDirection[direction], `${where}.frames.${direction}`, LOOK_LAYOUT_LIMITS.frames);
    if (listed.length === 0) {
      throw new Error(`${where}.frames.${direction} must hold a frame`);
    }
    frames[direction] = listed.map((frame, index) => {
      if (typeof frame !== "number" || !Number.isSafeInteger(frame) || frame < 0) {
        throw new Error(`${where}.frames.${direction}[${index}] must be a whole number of at least 0`);
      }
      if (frame >= cells) {
        throw new Error(`${where}.frames.${direction}[${index}] names cell ${frame} of ${cells}`);
      }
      return frame;
    });
  }
  return { fps, frames };
}

/**
 * Reads the strips into a record with no prototype, each under a plain name.
 *
 * @param value The parsed strips.
 * @param cells How many cells the layout holds.
 * @returns The strips, in the order listed.
 * @throws Error when there are too many, a name is not a strip's name, or a strip does not read (`stripOf`).
 */
function stripsOf(value: unknown, cells: number): Record<string, LookLayoutStrip> {
  const entries = Object.entries(asObject(value, "look layout strips", null));
  if (entries.length > LOOK_LAYOUT_LIMITS.strips) {
    throw new Error(`look layout strips must hold at most ${LOOK_LAYOUT_LIMITS.strips}`);
  }
  const strips = Object.create(null) as Record<string, LookLayoutStrip>;
  for (const [name, strip] of entries) {
    if (!STRIP_NAME.test(name) || REFUSED_STRIP_NAMES.includes(name)) {
      throw new Error(`look layout strips: ${JSON.stringify(name.slice(0, 40))} is not a strip's name`);
    }
    strips[name] = stripOf(strip, `look layout strips.${name}`, cells);
  }
  return strips;
}

/**
 * Reads one layer of the drawing order.
 *
 * @param value The parsed layer.
 * @param where Its name, for the error.
 * @returns The layer.
 * @throws Error when it holds an unknown field, its slot is no look slot or its rank neither null nor a rank.
 */
function layerOf(value: unknown, where: string): LookComposeLayer {
  const fields = asObject(value, where, ["slot", "rank"]);
  const slot = fields["slot"];
  if (typeof slot !== "string" || !(LOOK_SLOTS as readonly string[]).includes(slot)) {
    throw new Error(`${where}.slot must be a look slot`);
  }
  const rank = fields["rank"];
  if (rank !== null && (typeof rank !== "string" || !(LOOK_RANKS as readonly string[]).includes(rank))) {
    throw new Error(`${where}.rank must be null or a rank`);
  }
  return { slot: slot as LookSlot, rank: rank as LookRank | null };
}

/**
 * Validates a parsed `layout.json` (`LookLayout`).
 *
 * @param value The parsed document.
 * @returns The layout, rebuilt in the type's field order, its strips in a record with no prototype.
 * @throws Error naming the first part at fault: another version, a field the layout does not know, a number or a
 *   count past its bound (`LOOK_LAYOUT_LIMITS`), a cell outside the block, a strip whose name is not plain or that
 *   lacks a rate or a direction, a frame that names no cell, ranks other than `LOOK_RANKS` in order, or a layer of
 *   the drawing order with an unknown slot or rank.
 */
export function validateLookLayout(value: unknown): LookLayout {
  const raw = asObject(value, "look layout", null);
  if (raw["version"] !== LOOK_LAYOUT_VERSION) {
    throw new Error(`look layout version ${String(raw["version"])} is not ${LOOK_LAYOUT_VERSION}`);
  }
  const fields = asObject(value, "look layout", ["version", "block", "gutter", "cells", "strips", "ranks", "compose", "head"]);
  const size = asObject(fields["block"], "look layout block", ["width", "height"]);
  const block = { width: whole(size["width"], "look layout block.width", 1, LOOK_LAYOUT_LIMITS.block), height: whole(size["height"], "look layout block.height", 1, LOOK_LAYOUT_LIMITS.block) };
  const gutter = whole(fields["gutter"], "look layout gutter", 0, LOOK_LAYOUT_LIMITS.gutter);
  const cells = asArray(fields["cells"], "look layout cells", LOOK_LAYOUT_LIMITS.cells).map((cell, index) => cellOf(cell, `look layout cells[${index}]`, block));
  const strips = stripsOf(fields["strips"], cells.length);
  const ranks = asArray(fields["ranks"], "look layout ranks", LOOK_RANKS.length);
  if (ranks.length !== LOOK_RANKS.length || ranks.some((rank, index) => rank !== LOOK_RANKS[index])) {
    throw new Error(`look layout ranks must be ${LOOK_RANKS.join(", ")}`);
  }
  const compose = asArray(fields["compose"], "look layout compose", LOOK_LAYOUT_LIMITS.compose).map((layer, index) => layerOf(layer, `look layout compose[${index}]`));
  const head = whole(fields["head"], "look layout head", 0, LOOK_LAYOUT_LIMITS.block);
  return { version: LOOK_LAYOUT_VERSION, block, gutter, cells, strips, ranks: [...LOOK_RANKS], compose, head };
}

/**
 * Whether a name is a sheet's id: a piece's key, or a key and a rank.
 *
 * @param id The name.
 * @returns True when it is.
 */
function isSheetId(id: string): boolean {
  const dot = id.indexOf(".");
  const key = dot === -1 ? id : id.slice(0, dot);
  return LOOK_KEY_PATTERN.test(key) && (dot === -1 || (LOOK_RANKS as readonly string[]).includes(id.slice(dot + 1)));
}

/**
 * Validates a parsed look sheets' pointer (`LookManifest`). It checks the shape alone; whether the pointer holds the
 * sheets of a looks table is `lookManifestProblems`.
 *
 * @param value The parsed document.
 * @returns The pointer, rebuilt in the type's field order with its sheets in the order they were listed.
 * @throws Error when the pointer holds a field it does not know, the layout does not validate (`validateLookLayout`),
 *   there are more sheets than `LOOK_LAYOUT_LIMITS.sheets`, a sheet's name is not a sheet id or its path is not that
 *   id with a 16 digit hash under `assets/looks`, or the catalogue's path is not the hashed catalogue under
 *   `assets/looks`.
 */
export function validateLookManifest(value: unknown): LookManifest {
  const fields = asObject(value, "look manifest", ["layout", "sheets", "catalogue"]);
  const layout = validateLookLayout(fields["layout"]);
  const listed = Object.entries(asObject(fields["sheets"], "look manifest sheets", null));
  if (listed.length > LOOK_LAYOUT_LIMITS.sheets) {
    throw new Error(`look manifest sheets must hold at most ${LOOK_LAYOUT_LIMITS.sheets}`);
  }
  const sheets: Record<string, string> = {};
  for (const [id, path] of listed) {
    const prefix = `${LOOK_SHEETS_DIR}/${id}.`;
    if (!isSheetId(id) || typeof path !== "string" || !path.startsWith(prefix) || !path.endsWith(".png") || !SHEET_HASH.test(path.slice(prefix.length, -".png".length))) {
      throw new Error(`look manifest sheets.${id.slice(0, 40)} must be ${prefix.slice(0, 60)}<16 hex>.png for a sheet id`);
    }
    sheets[id] = path;
  }
  const catalogue = fields["catalogue"];
  if (typeof catalogue !== "string" || !CATALOGUE_PATH.test(catalogue)) {
    throw new Error(`look manifest catalogue must be ${LOOK_SHEETS_DIR}/catalogue.<16 hex>.json`);
  }
  return { layout, sheets, catalogue };
}

/**
 * Holds a pointer against a looks table: every piece of the table has each of its sheets (`lookSheetIds`), and the
 * pointer names no sheet of anything else. A publisher runs it before it uploads, since a pointer that lacks a piece
 * leaves every character wearing it undressed, and a stale or hand-edited one would otherwise pass on shape alone.
 *
 * @param pieces The looks table's pieces.
 * @param manifest The validated pointer.
 * @returns One phrase per fault, the pieces short of a sheet first, in the table's order, then the sheets of no
 *   piece, in the pointer's order; empty when the pointer covers the table exactly.
 * @example
 * lookManifestProblems(pieces, { ...manifest, sheets: {} });
 * // ["humn-1 (skin) lacks humn-1", ...]
 */
export function lookManifestProblems(pieces: readonly LookPiece[], manifest: LookManifest): string[] {
  const problems: string[] = [];
  const wanted = new Set<string>();
  for (const piece of pieces) {
    const ids = lookSheetIds(piece.slot, piece.key);
    for (const id of ids) {
      wanted.add(id);
    }
    const missing = ids.filter((id) => !Object.hasOwn(manifest.sheets, id));
    if (missing.length > 0) {
      problems.push(`${piece.key} (${piece.slot}) lacks ${missing.join(", ")}`);
    }
  }
  for (const id of Object.keys(manifest.sheets)) {
    if (!wanted.has(id)) {
      problems.push(`${id} is no sheet of a piece of the looks table`);
    }
  }
  return problems;
}
